import type { Lead } from '@up2stay/up2promo-api-client';
import { LeadsApi } from '@up2stay/up2promo-api-client';
import { toast } from 'sonner';

import {
  type LeadBoardItem,
  buildIdsByStatus,
  isSameBoardLayout,
} from '@/core/components/leads/leads-board-item';
import { LEAD_STATUS_IDS } from '@/core/components/leads/lead-status-colors';
import { triggerSelectionHaptic } from '@/core/lib/dnd-haptics';

/** Max concurrent PATCH calls when persisting the board after drag (avoids sequential N×latency). */
export const PERSIST_BOARD_UPDATE_CONCURRENCY = 6;

export async function runWithConcurrencyLimit<T>(
  items: readonly T[],
  limit: number,
  fn: (item: T) => Promise<unknown>,
): Promise<void> {
  if (items.length === 0) return;
  const cap = Math.max(1, limit);
  const executing = new Set<Promise<unknown>>();
  const tasks: Promise<unknown>[] = [];

  for (const item of items) {
    const p = Promise.resolve().then(() => fn(item));
    tasks.push(p);
    executing.add(p);
    void p.finally(() => {
      executing.delete(p);
    });
    if (executing.size >= cap) {
      await Promise.race(executing);
    }
  }
  await Promise.all(tasks);
}

export type LeadBoardPersistPatch = {
  id: string;
  body: { status?: Lead['status']; order?: number };
};

export function buildLeadBoardPersistPatches(
  finalData: LeadBoardItem[],
  leadsById: Map<string, Lead>,
): LeadBoardPersistPatch[] {
  const idsByStatus = buildIdsByStatus(finalData);
  const patches: LeadBoardPersistPatch[] = [];

  for (const statusId of LEAD_STATUS_IDS) {
    const ids = idsByStatus.get(statusId) ?? [];
    for (let i = 0; i < ids.length; i++) {
      const id = ids[i];
      if (!id) continue;
      const lead = leadsById.get(id);
      if (!lead) continue;
      const needsStatus = lead.status !== statusId;
      const needsOrder = (lead.order ?? null) !== i;
      if (!needsStatus && !needsOrder) continue;
      patches.push({
        id,
        body: {
          ...(needsStatus ? { status: statusId } : {}),
          ...(needsOrder ? { order: i } : {}),
        },
      });
    }
  }

  return patches;
}

export async function persistLeadBoardUpdates(options: {
  finalData: LeadBoardItem[];
  startSnapshot: LeadBoardItem[] | null;
  leadsById: Map<string, Lead>;
  workspaceId: string | undefined;
  statusUpdateFailedMessage: string;
  invalidate: () => void;
}): Promise<void> {
  const {
    finalData,
    startSnapshot,
    leadsById,
    workspaceId,
    statusUpdateFailedMessage,
    invalidate,
  } = options;

  if (startSnapshot && isSameBoardLayout(startSnapshot, finalData)) {
    return;
  }

  const patches = buildLeadBoardPersistPatches(finalData, leadsById);

  try {
    await runWithConcurrencyLimit(
      patches,
      PERSIST_BOARD_UPDATE_CONCURRENCY,
      ({ id, body }) =>
        LeadsApi.update({
          path: {
            workspaceId: workspaceId as string,
            id,
          },
          body,
        }),
    );
    void triggerSelectionHaptic();
    invalidate();
  } catch {
    toast.error(statusUpdateFailedMessage);
    invalidate();
  }
}
